import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from "framer-motion"
import { tokens } from '@/locales/translations/tokens';
import { CardData } from '../../types';
import { RelevanceScore } from '../../relevance/relevance-score';
import { useGlobalSourceManagementStore } from '@/stores/globalSourceManagementStore';

interface VideoContentProps {
  segmentTimestamps?: Array<{
    text: string;
    startInSeconds: number;
    endInSeconds: number;
  }>;
  title: string;
  groupedCards: CardData[] | undefined;
  currentCardIndex: number;
  relevanceValue: number;
}

export const VideoContent: React.FC<VideoContentProps> = ({ segmentTimestamps, title, groupedCards, currentCardIndex, relevanceValue }) => {
  const { t } = useTranslation();
  const globalSourceManager = useGlobalSourceManagementStore();
  function handleVideoUpdateTime(time: number) {
    globalSourceManager.setVideoTime(time);
  }
  return (
    <div className="space-y-4">
      <section className='flex justify-between items-center'>
        <div className='flex flex-col gap-y-2'>
          <h2 className='line-clamp-2 text-xl font-semibold text-white sm:text-2xl'>
            {title}
          </h2>
          {groupedCards && groupedCards.length > 1 && (
            <span className='inline-flex w-fit items-center gap-1 rounded-md bg-primary/40 px-2.5 py-0.5 text-xs font-medium text-white'>
              <motion.span
                key={currentCardIndex}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                {currentCardIndex + 1}
              </motion.span>
              <span className='opacity-70'>{t(tokens.ui.common.of)}</span>
              <span>{groupedCards.length}</span>
            </span>
          )}
        </div>
        <div className={`bottom-4 right-4 z-20 rounded-lg p-2`}>
          <RelevanceScore value={relevanceValue} />
        </div>
      </section>
      {segmentTimestamps && segmentTimestamps.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold mb-3">{t(tokens.workstation.sources.segmentTimestamps)}</h3>
          <div className="grid gap-2">
            {segmentTimestamps.map((segment, idx) => (
              <div
                key={idx}
                className="group relative bg-muted/30 hover:bg-muted/60 border border-border/50 hover:border-border rounded-lg p-4 transition-all duration-200 hover:cursor-pointer hover:shadow-sm"
                onClick={() => handleVideoUpdateTime(segment.startInSeconds)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground leading-relaxed mb-2 group-hover:text-primary transition-colors">
                      {segment.text}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        {t(tokens.workstation.sources.videoStart)}: {Math.floor(segment.startInSeconds / 60)}:
                        {(segment.startInSeconds % 60).toString().padStart(2, "0")}
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                        {t(tokens.workstation.sources.videoEnd)}: {Math.floor(segment.endInSeconds / 60)}:
                        {(segment.endInSeconds % 60).toString().padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <div className="w-0 h-0 border-l-[4px] border-l-primary border-y-[3px] border-y-transparent ml-0.5"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};